import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import ServiceSelector from '../components/ServiceSelector';
import { RepairService, Model } from '../types/BookingTypes';
import { getServicesByModelId } from '../services/serviceService';
import { useCart } from '../context/CartContext';
import { isLoggedIn } from '../services/authService';
import { generateSEOContent, updatePageSEO, resetPageSEO } from '../utils/seoUtils';
import ModelRepairsCarousel from '../components/ModelRepairsCarousel';

const ServicePage: React.FC = () => {
  const { modelId } = useParams<{ modelId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { refreshCart, cart } = useCart();
  
  // Get model from either current state or will be reconstructed from services
  const [model, setModel] = useState<Model | null>(location.state?.model as Model || null);
  
  const [services, setServices] = useState<RepairService[]>([]);
  const [selectedServices, setSelectedServices] = useState<RepairService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Create a stable hash of cart items for this model to prevent unnecessary re-renders
  const cartHash = useMemo(() => {
    if (!model) return '';
    const modelCartItem = cart.find(item => item.model.id === model.id);
    return modelCartItem?.services?.map(s => `${s.id}-${s.cartItemId}`).join(',') || '';
  }, [cart, model?.id]);

  // Initialize selected services from cart when cart changes for this model
  useEffect(() => {
    if (model) {
      const modelCartItem = cart.find(item => item.model.id === model.id);
      const cartServices = modelCartItem?.services?.map(service => {
        const { cartItemId, ...repairService } = service;
        return repairService as RepairService;
      }) || [];
      
      setSelectedServices(cartServices);
    }
  }, [cartHash, model?.id]); // Use stable cartHash instead of cart

  useEffect(() => {
    const fetchServices = async () => {
      if (!modelId) return;
      
      try {
        setLoading(true);
        const fetchedServices = await getServicesByModelId(parseInt(modelId));
        setServices(fetchedServices);
        
        // Reconstruct model if needed and format image URL
        let reconstructedModel: Model | null = null;
        
        // If no model was passed via navigation state, reconstruct it from the first service
        if (!model && fetchedServices.length > 0) {
          const firstService = fetchedServices[0];
          
          reconstructedModel = {
            id: firstService.modelId,
            name: firstService.modelName,
            image: firstService.modelImage || '', // Use properly formatted URL from API or empty string
            brandId: firstService.makeId,
            brand: firstService.makeName
          };
          setModel(reconstructedModel);
          
          // Update SEO for reconstructed model
          const seoContent = generateSEOContent(reconstructedModel, fetchedServices, modelId);
          updatePageSEO(seoContent);
        } else if (model) {
          // Update SEO for existing model
          const seoContent = generateSEOContent(model, fetchedServices, modelId);
          updatePageSEO(seoContent);
        }
        
        // If coming back from login with pre-selected services, restore them
        if (location.state?.selectedServices) {
          const restoredServices = location.state.selectedServices as RepairService[];
          setSelectedServices(restoredServices);
          
          // Clear the state to prevent re-triggering on subsequent renders
          navigate(location.pathname, { 
            state: { model: model || reconstructedModel }, 
            replace: true 
          });
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch services');
      } finally {
        setLoading(false);
      }
    };

    fetchServices();
  }, [modelId, location.state, navigate, model]);

  // Update SEO when model or services change
  useEffect(() => {
    if (model && services.length > 0 && modelId) {
      const seoContent = generateSEOContent(model, services, modelId);
      updatePageSEO(seoContent);
    }
  }, [model, services, modelId]);

  // Refresh cart when user is logged in
  useEffect(() => {
    if (isLoggedIn()) {
      refreshCart();
    }
  }, [refreshCart]);

  // Cleanup SEO changes when component unmounts
  useEffect(() => {
    return () => {
      resetPageSEO();
    };
  }, []);

  const handleBack = () => {
    // Navigate to the brand page if model exists, otherwise go to home
    if (model) {
      navigate(`/brand/${model.brandId}`);
    } else {
      navigate('/');
    }
  };

  const handleServiceSelect = (services: RepairService[]) => {
    setSelectedServices(services);
  };

  if (!model && !loading) {
    return <div className="p-8 text-center text-red-500">Model information not found</div>;
  }

  if (loading) {
    return (
      <div className="p-8 flex flex-col items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-minto-500 mx-auto"></div>
        <div className="mt-4 text-gray-500">Loading...</div>
      </div>
    );
  }

  if (error) {
    return <div className="p-8 text-center text-red-500">{error}</div>;
  }

  // At this point, model should exist or we would have returned early
  if (!model) {
    return <div className="p-8 text-center text-red-500">Model information not found</div>;
  }

  return (
    <div className="p-2">
      <ServiceSelector
        services={services}
        selectedServices={selectedServices}
        onSelect={handleServiceSelect}
        onBack={handleBack}
        modelName={model.name}
        model={model}
      />

      {/* Recent repairs for this model */}
      {model && modelId && (
        <div className="mb-2">
          <ModelRepairsCarousel
            modelId={parseInt(modelId)}
            modelName={model.name}
          />
        </div>
      )}

      {/* Service area cross-links for SEO */}
      <div className="mx-2 mb-4 mt-2 bg-gray-50 border border-gray-100 rounded-xl p-4">
        <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
          <i className="fas fa-map-marker-alt text-minto-400 mr-1"></i>
          Doorstep Repair Available In
        </p>
        <div className="flex flex-wrap gap-2">
          {[
            { label: 'Andheri', to: '/mobile-repair-andheri' },
            { label: 'Bandra', to: '/mobile-repair-bandra' },
            { label: 'Borivali', to: '/mobile-repair-borivali' },
            { label: 'Dadar', to: '/mobile-repair-dadar' },
          ].map((area) => (
            <Link
              key={area.to}
              to={area.to}
              className="text-xs px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-600 hover:border-minto-400 hover:text-minto-600 transition-colors"
            >
              {area.label}
            </Link>
          ))}
          <span className="text-xs px-3 py-1 rounded-full bg-white border border-gray-200 text-gray-500">
            & all Mumbai areas
          </span>
        </div>
      </div>
    </div>
  );
};

export default ServicePage; 